import { z } from 'zod';
import {
  AssetLifecycleSchema,
  AssetTagSchema,
  EndpointApiTypeSchema,
  EndpointSourceSchema,
  HttpMethodSchema,
} from '@terra/shared-schemas-orchestrator';
import { ENVIRONMENT_TYPES } from '@terra/api-common-constants';
import {
  LifecycleStateFilterSchema,
  OptionalBooleanQueryParamSchema,
} from '@terra/shared-dto-types';

export const UnverifiedAssetStatusSchema = z.enum([
  'FAILED',
  'BLOCKED',
  'REJECTED',
  'PENDING',
]);

export const AssetsParamsSchema = z.object({
  applicationId: z.string().describe('The application id'),
});

export const AssetsOrderByFieldSchema = z.enum([
  'updatedAt',
  'createdAt',
  'url',
]);

export const AssetsOrderBySchema = z.object({
  field: AssetsOrderByFieldSchema,
  order: z.enum(['asc', 'desc']),
});

export const AssetsDateRangeBoundarySchema = z
  .enum(['day', 'exact'])
  .describe(
    'How created/updated date filters are applied. day (default) widens each date to the whole UTC day; exact uses the timestamps as sent.',
  );

export const AssetsQuerySchema = z.object({
  url: z
    .string()
    .optional()
    .describe('Filter assets by URL (case-insensitive partial match)'),
  method: HttpMethodSchema.optional().describe(
    'Filter endpoints by a single HTTP method. Prefer methods when filtering by more than one.',
  ),
  methods: HttpMethodSchema.array()
    .optional()
    .describe(
      'Filter endpoints by one or more HTTP methods (GET, POST, PUT, DELETE, PATCH) - only applies to endpoint assets',
    ),
  type: z
    .enum(['ALL', 'endpoints', 'webpages', 'agents'])
    .optional()
    .default('ALL')
    .describe('Filter by asset type'),
  isVerified: z
    .boolean()
    .optional()
    .describe(
      'Sub-filter of endpoints. true/omitted returns confirmed endpoints; false returns showable unverified candidates. Ignored for webpages and agents.',
    ),
  unverifiedStatuses: UnverifiedAssetStatusSchema.array()
    .optional()
    .describe(
      'Filter unverified endpoint candidates by verification status. Only applied when isVerified is false.',
    ),
  createdStartDate: z.coerce
    .date()
    .optional()
    .describe('Filter assets created on or after this date'),
  createdEndDate: z.coerce
    .date()
    .optional()
    .describe('Filter assets created on or before this date'),
  updatedStartDate: z.coerce
    .date()
    .optional()
    .describe('Filter assets updated on or after this date'),
  updatedEndDate: z.coerce
    .date()
    .optional()
    .describe('Filter assets updated on or before this date'),
  dateRangeBoundary: AssetsDateRangeBoundarySchema.optional(),
  domainFilters: z
    .array(
      z.object({
        host: z.string().describe('Domain hostname to filter by (exact match)'),
        pathPrefix: z
          .string()
          .optional()
          .describe('URL path prefix with segment-boundary semantics'),
        method: z
          .enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'])
          .optional()
          .describe('HTTP method to filter by (endpoints only)'),
      }),
    )
    .optional()
    .describe(
      'Domain tree selection filters — each entry specifies a host, optional path prefix, and optional method for server-side filtering',
    ),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .describe('Maximum number of assets to return'),
  skip: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Number of assets to skip (for pagination)'),
  orderBy: z
    .array(AssetsOrderBySchema)
    .optional()
    .describe(
      'Sort order for assets. Supports sorting by updatedAt, createdAt, or url. Defaults to updatedAt desc.',
    ),
  withCodeGraph: z
    .boolean()
    .optional()
    .describe(
      'When true, return only endpoints that have an associated code graph',
    ),
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter by asset lifecycle state (active, archived, or all)',
  ),
});

export const AssetPreviewMetricsSchema = z.object({
  activeFindingsCount: z.int().nonnegative().optional(),
  inactiveFindingsCount: z.int().nonnegative().optional(),
  activeSignalsCount: z.int().nonnegative().optional(),
  inactiveSignalsCount: z.int().nonnegative().optional(),
});

export const AssetRelatedWebpagePreviewSchema = z.object({
  id: z.string(),
  url: z.string(),
  signedUrl: z.string().nullable(),
});

export const AssetPreviewSchema = z.discriminatedUnion('assetType', [
  z.object({
    assetType: z.literal('endpoint'),
    id: z.string(),
    description: z.string().nullable(),
    path: z.string(),
    host: z.string(),
    port: z.int().nullable(),
    url: z.string(),
    request: z.object({
      method: z.enum(['GET', 'POST', 'DELETE', 'PUT', 'PATCH']),
      pathParams: z.array(z.string()).optional(),
      bodySchema: z.string().nullable().optional(),
    }),
    graphqlOperationName: z.string().nullable().optional(),
    xmlOperationName: z.string().nullable().optional(),
    xmlOperationDisplayName: z.string().nullable().optional(),
    mcpOperationName: z.string().nullable().optional(),
    codeGraphId: z.string().nullable(),
    graphCheckFailed: z.boolean().nullable().optional(),
    graphCheckFailedReason: z.string().nullable().optional(),
    endpointAssetTags: AssetTagSchema.array().optional(),
    sources: EndpointSourceSchema.array().optional(),
    aiFlowGraphId: z.string().nullable().optional(),
    webpageIds: z.array(z.string()).optional().default([]),
    relatedWebpagesCount: z
      .int()
      .nonnegative()
      .optional()
      .describe(
        'Webpages related to this endpoint, including pages matched from captured traffic',
      ),
    relatedWebpagePreview: AssetRelatedWebpagePreviewSchema.nullable()
      .optional()
      .describe(
        'First related webpage with a signed screenshot URL, for list thumbnails',
      ),
    credentialRoles: z.array(z.string()).optional(),
    metrics: AssetPreviewMetricsSchema.optional(),
    lifecycle: AssetLifecycleSchema.optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  }),
  z.object({
    assetType: z.literal('webpage'),
    id: z.string(),
    url: z.string(),
    description: z.string().nullable(),
    signedUrl: z.string().nullable(),
    role: z.string().optional(),
    linksInternal: z.array(z.string()).optional(),
    linksExternal: z.array(z.string()).optional(),
    endpointsCount: z.int().nonnegative().optional().default(0),
    lifecycle: AssetLifecycleSchema.optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  }),
  z.object({
    assetType: z.literal('unverifiedEndpoint'),
    id: z.string(),
    path: z.string(),
    host: z.string(),
    port: z.int().nullable(),
    url: z.string(),
    request: z.object({
      method: z.enum(['GET', 'POST', 'DELETE', 'PUT', 'PATCH']),
    }),
    apiType: EndpointApiTypeSchema,
    unverifiedStatus: UnverifiedAssetStatusSchema,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  }),
]);

export const AssetsPreviewTypeCountsSchema = z.object({
  endpoints: z.int().nonnegative(),
  webpages: z.int().nonnegative(),
  agents: z.int().nonnegative(),
  unverified: z.int().nonnegative(),
});

export const AssetsPreviewReturnSchema = z.object({
  assets: z.array(AssetPreviewSchema).describe('The unified assets preview'),
  totalCount: z
    .int()
    .nonnegative()
    .describe('Total number of assets matching the filter'),
  typeCounts: AssetsPreviewTypeCountsSchema.optional(),
});

export const ErrorResponseSchema = z.object({
  message: z.string().describe('Error message'),
});

export const AssetsTrendQuerySchema = z.object({
  from: z
    .string()
    .optional()
    .describe('Start date for the trend period (ISO 8601 format)'),
  to: z
    .string()
    .optional()
    .describe('End date for the trend period (ISO 8601 format)'),
  environment: z
    .enum(ENVIRONMENT_TYPES)
    .optional()
    .describe('Filter by application environment'),
  businessUnitId: z.string().optional().describe('Filter by business unit ID'),
});

export const AssetsTrendDataSchema = z.object({
  date: z.string().describe('Date in YYYY-MM-DD format'),
  endpoints: z.number().describe('Count of endpoints detected by this date'),
  webPages: z.number().describe('Count of web pages detected by this date'),
});

export const AssetsTrendResponseSchema = z.object({
  data: z.array(AssetsTrendDataSchema),
});

export const DomainTreeNodeTypeSchema = z.enum([
  'appLevel',
  'domain',
  'path',
  'endpoint',
  'webpage',
]);

const parseQueryBoolean = (
  val: unknown,
  { treatAbsentAsFalse = false }: { treatAbsentAsFalse?: boolean } = {},
): unknown => {
  if (val === true || val === 'true') {
    return true;
  }

  if (val === false || val === 'false') {
    return false;
  }

  if (val === undefined || val === null) {
    return treatAbsentAsFalse ? false : undefined;
  }

  return val;
};

const queryBooleanSchema = ({
  defaultValue,
  treatAbsentAsFalse = false,
}: {
  defaultValue: boolean;
  treatAbsentAsFalse?: boolean;
}) =>
  z.preprocess(
    (val) => parseQueryBoolean(val, { treatAbsentAsFalse }),
    z.boolean().optional().default(defaultValue),
  );

export const DomainTreeQuerySchema = z.object({
  includeMethodNodes: queryBooleanSchema({ defaultValue: true }).describe(
    'Include separate endpoint nodes per HTTP method and operation',
  ),
  applyApplicationLevelRoot: queryBooleanSchema({
    defaultValue: false,
    treatAbsentAsFalse: true,
  }).describe('Prepend synthetic application-level root node'),
  includeEndpoints: queryBooleanSchema({ defaultValue: true }).describe(
    'Include endpoints when building the domain tree',
  ),
  includeWebPages: queryBooleanSchema({ defaultValue: true }).describe(
    'Include web pages when building the domain tree',
  ),
  type: z
    .enum(['endpoints', 'agents', 'webpages', 'ALL'])
    .optional()
    .describe(
      'Filter tree assets by type. When omitted, includes all endpoints and web pages per include flags.',
    ),
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter by asset lifecycle state (active, archived, or all)',
  ),
});

export type DomainTreeNodeDtoSchemaType = {
  id: string;
  name: string;
  fullDomain: string;
  level: number;
  nodeType: z.infer<typeof DomainTreeNodeTypeSchema>;
  assetCount: number;
  fullPath?: string;
  method?: string;
  assetId?: string;
  isArchived: boolean;
  directEndpointIds: string[];
  directWebPageIds: string[];
  children: DomainTreeNodeDtoSchemaType[];
};

export const DomainTreeNodeDtoSchema: z.ZodType<DomainTreeNodeDtoSchemaType> =
  z.lazy(() =>
    z.object({
      id: z.string(),
      name: z.string(),
      fullDomain: z.string(),
      level: z.int().nonnegative(),
      nodeType: DomainTreeNodeTypeSchema,
      assetCount: z.int().nonnegative(),
      fullPath: z.string().optional(),
      method: z.string().optional(),
      assetId: z.string().optional(),
      isArchived: z.boolean(),
      directEndpointIds: z.array(z.string()),
      directWebPageIds: z.array(z.string()),
      children: z.array(DomainTreeNodeDtoSchema),
    }),
  );

export const DomainTreeResponseSchema = z.object({
  roots: z.array(DomainTreeNodeDtoSchema),
});

export const ApplicationEntityCountKindSchema = z.enum([
  'assets',
  'signals',
  'findings',
  'traces',
]);

export const ApplicationEntityCountModeSchema = z.enum(['total', 'typeCounts']);

export const ApplicationEntityCountsQuerySchema = z.object({
  kind: ApplicationEntityCountKindSchema,
  mode: ApplicationEntityCountModeSchema.optional().default('total'),
  lifecycleState: LifecycleStateFilterSchema.optional(),
  includeUnpublished: OptionalBooleanQueryParamSchema.describe(
    'Include unpublished findings in kind=findings totals (requires Execute Tests permission). Defaults to published-only.',
  ),
});

export const ApplicationEntityCountsResponseSchema = z.object({
  total: z.int().nonnegative(),
  typeCounts: AssetsPreviewTypeCountsSchema.optional(),
});

export type UnverifiedAssetStatus = z.infer<typeof UnverifiedAssetStatusSchema>;
export type AssetsTrendQuery = z.infer<typeof AssetsTrendQuerySchema>;
export type AssetsTrendData = z.infer<typeof AssetsTrendDataSchema>;
export type AssetsTrendResponse = z.infer<typeof AssetsTrendResponseSchema>;
